import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { PopupOptions } from '../../../shared/services/popup/popup-options';
import { ActivatedRoute, Router } from '@angular/router';
import { FormTemplate } from '../../../shared/models/question/form-template';
import { RaiseService } from '../../../core/services/api/raise.service';
import { ErrorService } from '../../../core/services/error.service';
import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { SubFormHandlerService } from '../../../shared/services/sub-form-handler/sub-form-handler.service';
import { finalize } from 'rxjs/operators';
import { FormType } from '../../../core/enum/form-type.enum';
import { Location } from '@angular/common';
import moment from 'moment';
import { MyApplicationFilterService } from '../my-application/my-application-filter.service';
import { MyApplicationFilter } from '../my-application/my-application-filter';
import { normalizeDuplicateFormKeys } from '../../../shared/utils/form-key-normalization';

@Component({
    selector: 'app-application-form',
    templateUrl: './application-form.component.html',
    styleUrls: ['./application-form.component.scss'],
    standalone: false
})
export class ApplicationFormComponent implements OnInit {
    formType: FormType;
    form: FormTemplate;
    applicationForm$: Observable<FormTemplate>;
    submissionId: number;
    stepId: number;
    fundRaiseApplicationId: number;
    submitText: string;

    FormType: typeof FormType = FormType;

    constructor(
        private subFormHandler: SubFormHandlerService,
        private popupService: PopupService,
        private router: Router,
        private raiseService: RaiseService,
        private errorService: ErrorService,
        private blockUiService: BlockUiService,
        private activatedRoute: ActivatedRoute,
        private location: Location,
        private myApplicationFilterService: MyApplicationFilterService,
    ) { }

    ngOnInit() {
        this.formType = this.activatedRoute.snapshot.data.type;
        this.submitText = this.formType == FormType.Fixed ? 'raise.submitApplication' : 'common.submit';

        if (this.formType != FormType.Fixed) {
            this.fundRaiseApplicationId = this.activatedRoute.snapshot.params.fundRaiseApplicationId;
            this.stepId = this.activatedRoute.snapshot.params.stepId;
        }

        if (this.formType == FormType.Submission || this.formType == FormType.Rejected) {
            this.submissionId = this.activatedRoute.snapshot.params.submissionId;
        }

        this.getForm();
    }

    getForm() {
        const observable =
            this.formType == FormType.Fixed
                ? this.raiseService.getRaiseFundForm()
                : this.formType == FormType.Submission || this.formType == FormType.Rejected
                    ? this.raiseService.getSubmisionForm(
                        this.fundRaiseApplicationId,
                        this.stepId,
                        this.submissionId,
                    )
                    : this.raiseService.getDynamicForm(this.fundRaiseApplicationId, this.stepId);

        observable.subscribe(
            (data) => {
                const form = data;

                const processFormData = (formData: any) => {
                    this.form = this.normalizeFormTemplate(formData);

                    this.form.formPanels?.forEach((formPanel) => {
                        formPanel.items?.forEach((item) => {
                            this.normalizeArrayItemValue(item);

                            if (this.formType == FormType.Submission) {
                                item.readonly = true;
                            }

                            if (
                                this.formType == FormType.Rejected &&
                                item.controlType == 'datepicker' &&
                                item.isToday
                            ) {
                                item.value = null;
                            }
                        });
                    });

                    if (this.formType == FormType.Dynamic) {
                        this.restoreDraft();
                    }

                    if (this.formType != FormType.Fixed) {
                        this.checkCommonKeys();
                    } else {
                        this.refreshApplicationForm();
                    }
                };

                if (form.content) {
                    let contentData: any;

                    if (typeof form.content === 'string') {
                        try {
                            contentData = JSON.parse(form.content);
                        } catch (err) {
                            this.errorService.showError(err);
                            this.back();
                            return;
                        }
                    } else {
                        contentData = form.content;
                    }

                    normalizeDuplicateFormKeys(contentData);
                    processFormData(contentData);
                } else if (form.url) {
                    this.raiseService.getApplicationForm(form.url).subscribe(
                        (remoteForm) => {
                            normalizeDuplicateFormKeys(remoteForm);
                            processFormData(remoteForm);
                        },
                        (err) => {
                            this.errorService.showError(err);
                            this.back();
                        },
                    );
                } else {
                    processFormData(form);
                }
            },
            (err) => {
                this.errorService.showError(err);
                this.back();
            },
        );
    }

    checkCommonKeys() {
        this.raiseService.getCommonKeys(this.fundRaiseApplicationId).subscribe(
            (keys) => {
                const commonkeys = keys && keys.values && keys.values.length > 0 ? keys.values : [];

                commonkeys.forEach((key) => {
                    this.form.title = this.form.title
                        ? this.form.title.replace('{' + key.dynamicFormKey + '}', key.value)
                        : null;

                    this.form.description = this.form.description
                        ? this.form.description.replace('{' + key.dynamicFormKey + '}', key.value)
                        : null;

                    if (this.form.formPanels && this.form.formPanels.length > 0) {
                        this.form.formPanels.forEach((formPanel) => {
                            if (formPanel.items && formPanel.items.length > 0) {
                formPanel.items.forEach((item: any) => {
                                    item.label = item.label
                                        ? item.label.replace('{' + key.dynamicFormKey + '}', key.value)
                                        : null;

                                    item.description = item.description
                                        ? item.description.replace('{' + key.dynamicFormKey + '}', key.value)
                                        : null;

                                    if (item.type === 'string') {
                                        item.value = item.value
                                            ? item.value.replace('{' + key.dynamicFormKey + '}', key.value)
                                            : null;
                                    }

                                    if (item.key === key.dynamicFormKey) {
                                        item.value = key.value;
                                        item.readonly = true;
                                    }
                                });
                            }
                        });
                    }
                });

                this.refreshApplicationForm();
            },
            (err) => {
                this.errorService.showError(err);
            },
        );
    }

    onSubFormTriggered(event: any): void {
        this.subFormHandler.onSubFormTriggered(
            event,
            () => this.form,
            (result) => this.parseTemplate(result),
            {
                blockSubFormChanges: this.formType == FormType.Submission,
                readonlyItems: this.formType == FormType.Submission,
                onFormUpdated: (updatedForm) => {
                    this.form = updatedForm;
                    this.refreshApplicationForm();
                },
            },
        );
    }

    private parseTemplate(form: any): any {
        let template: any = null;

        try {
            if (form?.content) {
                template = typeof form.content === 'string' ? JSON.parse(form.content) : form.content;
            } else if (form?.Content) {
                template = typeof form.Content === 'string' ? JSON.parse(form.Content) : form.Content;
            } else {
                template = form;
            }
        } catch (err) {
            this.errorService.showError(err);
            return null;
        }

        return this.normalizeFormTemplate(template);
    }

    private normalizeFormTemplate(template: any): any {
        if (!template) return template;

        const rawPanels = template.formPanels ?? template.FormPanels ?? [];

        return {
            title: template.title ?? template.Title ?? '',
            description: template.description ?? template.Description ?? '',
            formPanels: rawPanels.map((panel: any) => {
                const rawItems = panel.items ?? panel.Items ?? [];

                return {
                    title: panel.title ?? panel.Title ?? '',
                    description: panel.description ?? panel.Description ?? '',
                    items: rawItems.map((item: any) => {
                        const rawOptions = item.options ?? item.Options ?? [];

                        return {
                            ...item,
                            key: item.key ?? item.Key,
                            label: item.label ?? item.Label,
                            value: item.value ?? item.Value,
                            type: item.type ?? item.Type,
                            controlType: item.controlType ?? item.ControlType,
                            class: item.class ?? item.Class,
                            placeholder: item.placeholder ?? item.Placeholder,
                            description: item.description ?? item.Description,
                            hoverText: item.hoverText ?? item.HoverText,
                            readonly: item.readonly ?? item.Readonly,
                            required: item.required ?? item.Required,
                            rules: item.rules ?? item.Rules,
                            isToday: item.isToday ?? item.IsToday,
                            pattern: item.pattern ?? item.Pattern,
                            allowNegatives: item.allowNegatives ?? item.AllowNegatives,
                            options: rawOptions.map((option: any) => ({
                                ...option,
                                key: option.key ?? option.Key,
                                value: option.value ?? option.Value,
                                hasSub: option.hasSub ?? option.HasSub,
                                subForm: option.subForm ?? option.SubForm,
                            })),
                        };
                    }),
                };
            }),
        };
    }

    private refreshApplicationForm(): void {
        this.applicationForm$ = new Observable((observer) => {
            observer.next(this.form);
            observer.complete();
        });
    }

    // FRC-939: a session-expiry redirect mid-form (401 re-login-modal bailout, or
    // formerly any 403) used to wipe whatever the issuer had typed. Draft is saved
    // right before the save-step API call, restored on next load of the same step,
    // and cleared once that step actually saves successfully.
    private draftStorageKey(): string {
        return `raiseFormDraft:${this.fundRaiseApplicationId}:${this.stepId}`;
    }

    // Bounds how long a draft can outlive the session that wrote it — review flagged
    // unbounded localStorage retention of form data as an exposure window.
    private static readonly DRAFT_MAX_AGE_HOURS = 24;

    // Array items are serialized to a JSON string by submit() before saveDraft()
    // persists them, so restored drafts and freshly-loaded templates both need the
    // same string -> array normalization.
    private normalizeArrayItemValue(item: any): void {
        if (item.type !== 'array' || typeof item.value !== 'string') return;

        const trimmedValue = item.value.trim();

        if (trimmedValue === '') {
            const legacyValues = item.value
                .split(',')
                .map((v: string) => v.trim())
                .filter((v: string) => v.length > 0);

            if (legacyValues.length > 0) {
                item.value = legacyValues;
            }
        } else {
            try {
                const parsedValue = JSON.parse(trimmedValue);
                item.value = Array.isArray(parsedValue) ? parsedValue : [];
            } catch (e) {
                console.error('Parsing Failure:', item.key, e);
                item.value = [];
            }
        }
    }

    private saveDraft(): void {
        if (typeof window === 'undefined' || !this.form?.formPanels) return;

        try {
            const values = this.form.formPanels.flatMap((panel: any) =>
                (panel.items ?? []).map((item: any) => ({ key: item.key, value: item.value })),
            );

            localStorage.setItem(
                this.draftStorageKey(),
                JSON.stringify({ savedAt: moment().toISOString(), values }),
            );
        } catch (e) {
            // best-effort only — a failed draft save must not block submission
            console.error('Failed to save form draft:', e);
        }
    }

    private restoreDraft(): void {
        if (typeof window === 'undefined') return;

        try {
            const raw = localStorage.getItem(this.draftStorageKey());

            if (!raw) return;

            const draft: { savedAt: string; values: { key: string; value: any }[] } =
                JSON.parse(raw);

            if (moment().diff(moment(draft.savedAt), 'hours') >= ApplicationFormComponent.DRAFT_MAX_AGE_HOURS) {
                this.clearDraft();
                return;
            }

            const valueByKey = new Map(draft.values.map((v) => [v.key, v.value]));

            this.form.formPanels?.forEach((panel: any) => {
                panel.items?.forEach((item: any) => {
                    if (valueByKey.has(item.key)) {
                        item.value = valueByKey.get(item.key);
                        this.normalizeArrayItemValue(item);
                    }
                });
            });
        } catch (e) {
            // corrupt/unreadable draft — clear it so the next load starts fresh instead
            // of failing on the same stale entry every time
            console.error('Failed to restore form draft:', e);
            this.clearDraft();
        }
    }

    private clearDraft(): void {
        if (typeof window === 'undefined') return;

        try {
            localStorage.removeItem(this.draftStorageKey());
        } catch (e) {
            // best-effort — worst case a stale draft gets overwritten next save
            console.error('Failed to clear form draft:', e);
        }
    }

    submit(event: any) {
        this.form.formPanels.forEach((formPanel) => {
            if (formPanel.items && formPanel.items.length > 0) {
                formPanel.items.forEach((item: any) => {
                    if (item.key && item.key in event) {
                        if (event[item.key] != null) {
                            let finalValue: any;

                            if (item.controlType === 'datepicker') {
                                finalValue = moment(event[item.key]).toISOString();
                            } else {
                                finalValue =
                                    item.type === 'array' || Array.isArray(event[item.key])
                                        ? JSON.stringify(event[item.key])
                                        : event[item.key].toString();
                            }

                            item.value = finalValue;
                        } else {
                            item.value = null;
                        }
                    }
                });
            }
        });

        // Draft must be saved AFTER the sync above — it copies the just-submitted
        // event values onto this.form, which is what saveDraft() reads.
        if (this.formType == FormType.Dynamic) {
            this.saveDraft();
        }

        const missingRequiredFields: any[] = [];

        this.form.formPanels?.forEach((panel: any) => {
            panel.items?.forEach((item: any) => {
                const isRequired = item.rules?.required ?? item.required;

                if (!isRequired) return;

                const value = item.value;

                const isEmpty =
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (Array.isArray(value) && value.length === 0);

                if (isEmpty) {
                    missingRequiredFields.push({
                        panelTitle: panel.title,
                        key: item.key,
                        label: item.label,
                        controlType: item.controlType,
                        value: item.value,
                    });
                }
            });
        });

        const message =
            this.formType == FormType.Fixed
                ? 'raise.confirmApplicationMessage'
                : 'raise.confirmSubmitFormMessage';

        const titleLabel =
            this.formType == FormType.Fixed ? 'raise.confirmApplication' : 'common.confirmation';

        this.popupService
            .confirm(message, {
                titleLabel: titleLabel,
                okLabel: 'raise.yesSubmit',
                cancelLabel: 'raise.noBackToEdit',
            } as PopupOptions)
            .then(
                () => {
                    this.blockUiService.open();

                    const observable =
                        this.formType == FormType.Fixed
                            ? this.raiseService.submitApplicationForm(this.form)
                            : this.raiseService.submitDynamicForm(
                                this.fundRaiseApplicationId,
                                this.stepId,
                                this.form,
                            );

                    observable.pipe(finalize(() => this.blockUiService.close())).subscribe(
                        () => {
                            if (this.formType == FormType.Dynamic) {
                                this.clearDraft();
                            }

                            this.popupService.success('raise.successSubmitApplication').then(() => {
                                if (this.formType == FormType.Fixed) {
                                    this.myApplicationFilterService.filter = new MyApplicationFilter();
                                    this.router.navigate(['/raise/my-application']);
                                } else {
                                    this.back();
                                }
                            });
                        },
                        (err) => {
                            // FRC-939: a 403/authorization failure here must always surface
                            // something to the user (a save the interceptor no longer
                            // silently force-logs-out for) — fall back to a toast if the
                            // error body doesn't carry a displayable message.
                            this.errorService.showError(err, undefined, true);
                        },
                    );
                },
                () => { },
            );
    }

    confirmBack() {
        if (this.formType == FormType.Fixed || this.formType == FormType.Dynamic) {
            this.popupService.confirm('raise.confirmBack').then(
                () => {
                    this.back();
                },
                () => { },
            );
        } else {
            this.back();
        }
    }

    back() {
        // An explicit back-out of this step is the user abandoning whatever draft was
        // held for it — leaving it would silently reappear next time they load the step.
        if (this.formType == FormType.Dynamic) {
            this.clearDraft();
        }

        this.location.back();
    }

    print() {
        window.print();
    }
}
